import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Tooltip, Typography } from '@mui/material';
import BulkUploadActionsMenu from './BulkUploadActionsMenu';
import CustomPagination from '../../../components/customPagination';
import dayjs from 'dayjs';
import { useGetBulkUploadsHandler } from '../dataHandlers/getBulkUploadsHandler';
import { useGetBulkUploadTemplateConfigsHandler } from '../dataHandlers/getBulkUploadTemplateConfigsHandler';
import StatusChip from '../../../components/StatusChip';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import BulkUploadProcessedStatusBar from './BulkUploadProcessedStatusBar';

const bulkUploadCategoryMapping = {
  E_INVOICE_SALES: {
    label: 'Sales Documents',
  },
  E_INVOICE_PURCHASES: {
    label: 'Purchase Documents',
  },
  CUSTOMER: {
    label: 'Customer',
  },
  PRODUCT: {
    label: 'Products/Services',
  },
  SUPPLIER: {
    label: 'Supplier',
  },
};

const getCategoryKey = (category, subCategory) => {
  if (!category) return '';

  if ((category === 'E_INVOICE' || category === 'NON_E_INVOICE') && subCategory) {
    return `${category}_${subCategory}`;
  }

  return category;
};

const BulkUploadTable = ({ handlePaginationChange, searchQuery, showCreatedBy = false }) => {
  const { category, subCategory } = searchQuery ?? {};

  const { bulkUploads, totalDocuments, isLoading } = useGetBulkUploadsHandler({ searchQuery });

  const { getTemplateDisplayName } = useGetBulkUploadTemplateConfigsHandler({
    category,
    subCategory,
    silent: true,
  });

  const { bulkUpload } = useTranslatedMessages();

  const t = bulkUpload?.table ?? {};
  const col = t.columns ?? {};
  const status = t.status ?? {};

  const getCategoryConfig = (row) => {
    const key = getCategoryKey(row?.category, row?.subCategory);

    return (
      bulkUploadCategoryMapping[key] ||
      bulkUploadCategoryMapping[row?.category] || {
        label: row?.category ?? '',
      }
    );
  };

  const getCategoryLabel = (row) => {
    const config = getCategoryConfig(row);

    return config.label;
  };

  const getStatusConfig = (value) => {
    const normalizedStatus = value?.toUpperCase();

    const statusMapping = {
      COMPLETED: {
        color: 'success',
        label: status.processed ?? 'Processed',
      },
      PROCESSING: {
        color: 'warning',
        label: status.processing ?? 'Processing',
      },
      UPLOADED: {
        color: 'info',
        label: status.queued ?? 'Queued',
      },
      UPLOAD_FAILED: {
        color: 'error',
        label: 'Failed',
      },
    };

    return (
      statusMapping[normalizedStatus] || {
        color: 'default',
        label: value
          ?.split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' '),
      }
    );
  };

  const createdByColumn = {
    field: 'uploadedBy',
    headerName: col.created_by ?? 'Created By',
    flex: 1,
    renderCell: (params) => params.row.uploadedBy ?? '',
  };

  const columns = [
    {
      field: 'status',
      headerName: col.status ?? 'Status',
      flex: 1,
      id: 'bulk-upload-status',
      renderCell: ({ row }) => {
        const statusConfig = getStatusConfig(row?.status);

        return <StatusChip color={statusConfig.color} label={statusConfig.label} />;
      },
    },

    {
      field: 'createdOn',
      headerName: col.upload_time ?? 'Upload Time',
      flex: 1.2,
      valueFormatter: (params) => {
        const timestamp = params;

        if (!timestamp) return '';

        return dayjs(timestamp).format('DD-MM-YYYY hh:mm:ss A');
      },
    },

    {
      field: 'category',
      headerName: col.category ?? 'Category',
      flex: 1,
      renderCell: ({ row }) => getCategoryLabel(row),
    },

    {
      field: 'filename',
      headerName: col.file ?? col.file_name ?? 'File',
      flex: 1.5,
      renderCell: ({ row }) => {
        const templateDisplayName = getTemplateDisplayName(row?.templateName);

        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              width: '100%',
              py: 0.5,
              mt: 0.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                lineHeight: 1.3,
              }}
            >
              {row?.filename ?? ''}
            </Typography>

            {templateDisplayName && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: '0.75rem',
                  lineHeight: 1.2,
                }}
              >
                {templateDisplayName}
              </Typography>
            )}
          </Box>
        );
      },
    },

    {
      field: 'processedDocumentsCount',
      headerName: col.processed_status ?? 'Processed Status',
      flex: 1,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => {
        const normalizedStatus = row?.status?.toUpperCase();

        if (normalizedStatus === 'UPLOAD_FAILED') {
          return (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                cursor: 'pointer',
              }}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <Tooltip title="Failed to upload file" placement="bottom" enterTouchDelay={0}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                  N/A
                </Typography>
              </Tooltip>
            </Box>
          );
        }

        return (
          <BulkUploadProcessedStatusBar
            totalProcessedDocuments={row.processedDocumentsCount ?? 0}
            totalUploadedDocuments={row.totalDocumentsCount ?? 0}
          />
        );
      },
    },

    ...(showCreatedBy ? [createdByColumn] : []),

    {
      field: 'row',
      headerName: col.actions ?? 'Actions',
      flex: 0.5,
      sortable: false,
      renderCell: ({ row }) => <BulkUploadActionsMenu row={row} />,
    },
  ];

  return (
    <Box>
      <DataGrid
        loading={isLoading}
        rows={[...bulkUploads].sort((a, b) => {
          const dateA = new Date(a.created_on).toLocaleString();

          const dateB = new Date(b.created_on).toLocaleString();

          return new Date(dateB).getTime() - new Date(dateA).getTime();
        })}
        columns={columns}
        getRowId={(row) => row.id}
        paginationMode="server"
        rowCount={totalDocuments}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10, 25]}
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => {
          handlePaginationChange(model.page, model.pageSize);
        }}
        slots={{
          pagination: CustomPagination,
        }}
      />
    </Box>
  );
};

export default BulkUploadTable;
